export * from "./prospect-demo-reference.js";
export * from "./prospect-demo.js";
export * from "./canvas.js";
import { SOLUTION_CANVAS_CATALOG, validateSolutionCanvasDraft, } from "./canvas.js";
export const SOLUTION_CONTRACT_VERSION = "1.0.0";
const KEY = /^[a-z0-9](?:[a-z0-9._:-]{0,95})$/;
const NODE_KINDS = new Set([
    "channel",
    "conversation",
    "agent",
    "business",
    "knowledge",
    "automation",
    "human",
]);
const LIFECYCLES = new Set([
    "draft",
    "candidate",
    "certified",
]);
function objectValue(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value)
        ? value
        : null;
}
function nonEmpty(value, max = 256) {
    return typeof value === "string" &&
        value.trim().length > 0 &&
        value.trim().length <= max;
}
function issue(issues, code, path, message) {
    issues.push(Object.freeze({ code, path, message }));
}
function validateStringList(value, path, issues) {
    if (!Array.isArray(value) ||
        value.some((entry) => !nonEmpty(entry, 160) || !KEY.test(entry.trim()))) {
        issue(issues, "INVALID_STRING_LIST", path, path + " must contain canonical unique keys");
        return Object.freeze([]);
    }
    const normalized = value.map((entry) => entry.trim());
    if (new Set(normalized).size !== normalized.length) {
        issue(issues, "DUPLICATE_STRING_LIST_VALUE", path, path + " must not contain duplicate keys");
    }
    return Object.freeze(normalized);
}
export function validateSolutionBlueprint(value) {
    const issues = [];
    const root = objectValue(value);
    if (!root) {
        issue(issues, "INVALID_BLUEPRINT", "$", "solution blueprint must be an object");
        return Object.freeze({ valid: false, issues: Object.freeze(issues) });
    }
    if (root.schemaVersion !== 1) {
        issue(issues, "INVALID_SCHEMA_VERSION", "schemaVersion", "schemaVersion must be 1");
    }
    if (!nonEmpty(root.templateKey, 96) || !KEY.test(root.templateKey.trim())) {
        issue(issues, "INVALID_TEMPLATE_KEY", "templateKey", "templateKey must be canonical");
    }
    if (typeof root.version !== "number" ||
        !Number.isSafeInteger(root.version) ||
        root.version < 1) {
        issue(issues, "INVALID_VERSION", "version", "version must be a positive integer");
    }
    if (!nonEmpty(root.displayName, 120)) {
        issue(issues, "INVALID_DISPLAY_NAME", "displayName", "displayName is required");
    }
    if (!nonEmpty(root.description, 500)) {
        issue(issues, "INVALID_DESCRIPTION", "description", "description is required");
    }
    if (!nonEmpty(root.lifecycle) || !LIFECYCLES.has(root.lifecycle)) {
        issue(issues, "INVALID_LIFECYCLE", "lifecycle", "lifecycle is invalid");
    }
    if (root.executionAuthority !== "descriptive_only") {
        issue(issues, "EXECUTION_AUTHORITY_FORBIDDEN", "executionAuthority", "a Solution blueprint describes composition but cannot execute business actions");
    }
    const nodes = Array.isArray(root.nodes) ? root.nodes : [];
    if (nodes.length < 2 || nodes.length > 64) {
        issue(issues, "INVALID_NODE_COUNT", "nodes", "nodes must contain between 2 and 64 entries");
    }
    const nodeKeys = new Set();
    const declaredCapabilities = new Set();
    const declaredChannels = new Set();
    nodes.forEach((rawNode, index) => {
        const node = objectValue(rawNode);
        const path = "nodes[" + index + "]";
        if (!node) {
            issue(issues, "INVALID_NODE", path, "node must be an object");
            return;
        }
        if (!nonEmpty(node.key, 96) || !KEY.test(node.key.trim())) {
            issue(issues, "INVALID_NODE_KEY", path + ".key", "node key must be canonical");
        }
        else {
            const key = node.key.trim();
            if (nodeKeys.has(key)) {
                issue(issues, "DUPLICATE_NODE_KEY", path + ".key", "node key must be unique");
            }
            nodeKeys.add(key);
        }
        if (!nonEmpty(node.kind) || !NODE_KINDS.has(node.kind)) {
            issue(issues, "INVALID_NODE_KIND", path + ".kind", "node kind is invalid");
        }
        if (!nonEmpty(node.label, 120)) {
            issue(issues, "INVALID_NODE_LABEL", path + ".label", "node label is required");
        }
        if (!nonEmpty(node.eyebrow, 80)) {
            issue(issues, "INVALID_NODE_EYEBROW", path + ".eyebrow", "node eyebrow is required");
        }
        if (!nonEmpty(node.description, 240)) {
            issue(issues, "INVALID_NODE_DESCRIPTION", path + ".description", "node description is required");
        }
        if (node.owner != null && (!nonEmpty(node.owner, 120) || !KEY.test(node.owner.trim()))) {
            issue(issues, "INVALID_NODE_OWNER", path + ".owner", "owner must be canonical");
        }
        if (node.capabilityKey != null) {
            if (!nonEmpty(node.capabilityKey, 160) || !KEY.test(node.capabilityKey.trim())) {
                issue(issues, "INVALID_CAPABILITY_KEY", path + ".capabilityKey", "capabilityKey must be canonical");
            }
            else {
                declaredCapabilities.add(node.capabilityKey.trim());
            }
        }
        if (node.channelKey != null) {
            if (!nonEmpty(node.channelKey, 160) || !KEY.test(node.channelKey.trim())) {
                issue(issues, "INVALID_CHANNEL_KEY", path + ".channelKey", "channelKey must be canonical");
            }
            else {
                declaredChannels.add(node.channelKey.trim());
            }
        }
        if (node.kind === "business" && !nonEmpty(node.owner, 120)) {
            issue(issues, "BUSINESS_OWNER_REQUIRED", path + ".owner", "business nodes require a canonical owner");
        }
        if (node.kind === "business" && !nonEmpty(node.capabilityKey, 160)) {
            issue(issues, "BUSINESS_CAPABILITY_REQUIRED", path + ".capabilityKey", "business nodes require capabilityKey");
        }
        if (node.kind === "channel" && !nonEmpty(node.channelKey, 160)) {
            issue(issues, "CHANNEL_KEY_REQUIRED", path + ".channelKey", "channel nodes require channelKey");
        }
        if (node.layout != null) {
            const layout = objectValue(node.layout);
            if (!layout ||
                typeof layout.x !== "number" ||
                !Number.isFinite(layout.x) ||
                layout.x < 0 ||
                layout.x > 100 ||
                typeof layout.y !== "number" ||
                !Number.isFinite(layout.y) ||
                layout.y < 0 ||
                layout.y > 100) {
                issue(issues, "INVALID_NODE_LAYOUT", path + ".layout", "layout x/y must be numbers between 0 and 100");
            }
        }
    });
    const edges = Array.isArray(root.edges) ? root.edges : [];
    if (edges.length < 1 || edges.length > 128) {
        issue(issues, "INVALID_EDGE_COUNT", "edges", "edges must contain between 1 and 128 entries");
    }
    const edgeKeys = new Set();
    const edgePairs = new Set();
    const connectedNodes = new Set();
    edges.forEach((rawEdge, index) => {
        const edge = objectValue(rawEdge);
        const path = "edges[" + index + "]";
        if (!edge) {
            issue(issues, "INVALID_EDGE", path, "edge must be an object");
            return;
        }
        if (!nonEmpty(edge.key, 96) || !KEY.test(edge.key.trim())) {
            issue(issues, "INVALID_EDGE_KEY", path + ".key", "edge key must be canonical");
        }
        else {
            const key = edge.key.trim();
            if (edgeKeys.has(key))
                issue(issues, "DUPLICATE_EDGE_KEY", path + ".key", "edge key must be unique");
            edgeKeys.add(key);
        }
        const from = nonEmpty(edge.from, 96) ? edge.from.trim() : "";
        const to = nonEmpty(edge.to, 96) ? edge.to.trim() : "";
        if (!from || !nodeKeys.has(from)) {
            issue(issues, "UNKNOWN_EDGE_SOURCE", path + ".from", "edge source must reference a node");
        }
        if (!to || !nodeKeys.has(to)) {
            issue(issues, "UNKNOWN_EDGE_TARGET", path + ".to", "edge target must reference a node");
        }
        if (from && to && from === to) {
            issue(issues, "SELF_EDGE_FORBIDDEN", path, "self edges are not allowed");
        }
        if (from && to) {
            const pair = from + "->" + to;
            if (edgePairs.has(pair))
                issue(issues, "DUPLICATE_EDGE", path, "duplicate directed edges are not allowed");
            edgePairs.add(pair);
            connectedNodes.add(from);
            connectedNodes.add(to);
        }
    });
    for (const key of nodeKeys) {
        if (!connectedNodes.has(key)) {
            issue(issues, "ORPHAN_NODE", "nodes." + key, "every node must participate in the solution graph");
        }
    }
    const requirements = objectValue(root.requirements);
    if (!requirements) {
        issue(issues, "INVALID_REQUIREMENTS", "requirements", "requirements are required");
    }
    else {
        const capabilities = validateStringList(requirements.capabilities, "requirements.capabilities", issues);
        const channels = validateStringList(requirements.channels, "requirements.channels", issues);
        if (typeof requirements.humanControl !== "boolean") {
            issue(issues, "INVALID_HUMAN_CONTROL", "requirements.humanControl", "humanControl must be boolean");
        }
        for (const capability of declaredCapabilities) {
            if (!capabilities.includes(capability)) {
                issue(issues, "UNDECLARED_CAPABILITY_REQUIREMENT", "requirements.capabilities", "node capability " + capability + " must be declared as a requirement");
            }
        }
        for (const channel of declaredChannels) {
            if (!channels.includes(channel)) {
                issue(issues, "UNDECLARED_CHANNEL_REQUIREMENT", "requirements.channels", "node channel " + channel + " must be declared as a requirement");
            }
        }
        if (nodes.some((node) => objectValue(node)?.kind === "human") && requirements.humanControl !== true) {
            issue(issues, "HUMAN_CONTROL_REQUIREMENT_MISMATCH", "requirements.humanControl", "solutions with a human-control node must declare humanControl=true");
        }
    }
    return Object.freeze({
        valid: issues.length === 0,
        issues: Object.freeze(issues),
    });
}
export const AI_RECEPTIONIST_SOLUTION_V1 = Object.freeze({
    schemaVersion: 1,
    templateKey: "ai-receptionist-v1",
    version: 1,
    displayName: "Réceptionniste IA",
    description: "Canal entrant, conversation, agent, données métier, rendez-vous, automation, confirmation et contrôle humain.",
    lifecycle: "candidate",
    executionAuthority: "descriptive_only",
    nodes: Object.freeze([
        Object.freeze({ key: "phone", kind: "channel", label: "Téléphone", eyebrow: "Canal", description: "Appel entrant", channelKey: "voice", layout: Object.freeze({ x: 8, y: 42 }) }),
        Object.freeze({ key: "conversation", kind: "conversation", label: "Conversation", eyebrow: "Contexte", description: "Session et historique", owner: "conversation", capabilityKey: "conversation.context", layout: Object.freeze({ x: 25, y: 42 }) }),
        Object.freeze({ key: "agent", kind: "agent", label: "Agent IA", eyebrow: "Orchestration", description: "Intention et décisions", owner: "agent", capabilityKey: "agent.orchestration", layout: Object.freeze({ x: 43, y: 42 }) }),
        Object.freeze({ key: "customer", kind: "business", label: "Client", eyebrow: "Business truth", description: "Identité et contexte", owner: "customer", capabilityKey: "customer.identity.read", layout: Object.freeze({ x: 61, y: 15 }) }),
        Object.freeze({ key: "knowledge", kind: "knowledge", label: "Knowledge", eyebrow: "Grounding", description: "Règles et connaissances", owner: "knowledge", capabilityKey: "knowledge.query", layout: Object.freeze({ x: 61, y: 42 }) }),
        Object.freeze({ key: "scheduling", kind: "business", label: "Agenda", eyebrow: "Capability", description: "Disponibilités et rendez-vous", owner: "scheduling", capabilityKey: "scheduling.appointment", layout: Object.freeze({ x: 61, y: 69 }) }),
        Object.freeze({ key: "automation", kind: "automation", label: "Automation", eyebrow: "Workflow", description: "Actions post-rendez-vous", owner: "automation", capabilityKey: "automation.appointment-followup", layout: Object.freeze({ x: 79, y: 69 }) }),
        Object.freeze({ key: "whatsapp", kind: "channel", label: "WhatsApp", eyebrow: "Canal", description: "Confirmation client", channelKey: "meta-whatsapp", layout: Object.freeze({ x: 91, y: 69 }) }),
        Object.freeze({ key: "human", kind: "human", label: "Humain", eyebrow: "Control", description: "Reprise et validation", owner: "conversation", capabilityKey: "conversation.human-control", layout: Object.freeze({ x: 79, y: 18 }) }),
    ]),
    edges: Object.freeze([
        Object.freeze({ key: "phone-conversation", from: "phone", to: "conversation" }),
        Object.freeze({ key: "conversation-agent", from: "conversation", to: "agent" }),
        Object.freeze({ key: "agent-customer", from: "agent", to: "customer" }),
        Object.freeze({ key: "agent-knowledge", from: "agent", to: "knowledge" }),
        Object.freeze({ key: "agent-scheduling", from: "agent", to: "scheduling" }),
        Object.freeze({ key: "customer-human", from: "customer", to: "human" }),
        Object.freeze({ key: "scheduling-automation", from: "scheduling", to: "automation" }),
        Object.freeze({ key: "automation-whatsapp", from: "automation", to: "whatsapp" }),
    ]),
    requirements: Object.freeze({
        capabilities: Object.freeze([
            "conversation.context",
            "agent.orchestration",
            "customer.identity.read",
            "knowledge.query",
            "scheduling.appointment",
            "automation.appointment-followup",
            "conversation.human-control",
        ]),
        channels: Object.freeze(["voice", "meta-whatsapp"]),
        humanControl: true,
    }),
});
const AI_RECEPTIONIST_VALIDATION = validateSolutionBlueprint(AI_RECEPTIONIST_SOLUTION_V1);
if (!AI_RECEPTIONIST_VALIDATION.valid) {
    throw new Error("ai_receptionist_solution_invalid:" +
        AI_RECEPTIONIST_VALIDATION.issues.map((entry) => entry.code).join(","));
}
function normalizedCanvasLayouts(nodes) {
    if (nodes.length === 0)
        return new Map();
    const xs = nodes.map((node) => node.position.x);
    const ys = nodes.map((node) => node.position.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const spanX = Math.max(maxX - minX, 1);
    const spanY = Math.max(maxY - minY, 1);
    return new Map(nodes.map((node) => [
        node.id,
        Object.freeze({
            x: 8 + ((node.position.x - minX) / spanX) * 84,
            y: 10 + ((node.position.y - minY) / spanY) * 80,
        }),
    ]));
}
export function compileSolutionCanvasDraft(canvas, options = {}) {
    const canvasValidation = validateSolutionCanvasDraft(canvas);
    if (!canvasValidation.valid) {
        return Object.freeze({
            valid: false,
            blueprint: null,
            issues: canvasValidation.issues,
        });
    }
    const layouts = normalizedCanvasLayouts(canvas.nodes);
    const blueprintNodes = canvas.nodes.map((node) => {
        const catalog = SOLUTION_CANVAS_CATALOG[node.type];
        return Object.freeze({
            key: node.id,
            kind: catalog.kind,
            label: node.label.trim(),
            eyebrow: catalog.eyebrow,
            description: catalog.description,
            owner: catalog.owner ?? null,
            capabilityKey: catalog.capabilityKey ?? null,
            channelKey: catalog.channelKey ?? null,
            layout: layouts.get(node.id),
        });
    });
    const capabilities = [
        ...new Set(blueprintNodes
            .map((node) => node.capabilityKey)
            .filter((value) => Boolean(value))),
    ];
    const channels = [
        ...new Set(blueprintNodes
            .map((node) => node.channelKey)
            .filter((value) => Boolean(value))),
    ];
    const blueprint = Object.freeze({
        schemaVersion: 1,
        templateKey: options.templateKey ?? "custom-solution",
        version: options.version ?? 1,
        displayName: canvas.name.trim(),
        description: canvas.description.trim() ||
            "Solution composée dans ClickDialog Solution Studio.",
        lifecycle: "draft",
        executionAuthority: "descriptive_only",
        nodes: Object.freeze(blueprintNodes),
        edges: Object.freeze(canvas.edges.map((edge) => Object.freeze({
            key: edge.id,
            from: edge.source,
            to: edge.target,
        }))),
        requirements: Object.freeze({
            capabilities: Object.freeze(capabilities),
            channels: Object.freeze(channels),
            humanControl: blueprintNodes.some((node) => node.kind === "human"),
        }),
    });
    const validation = validateSolutionBlueprint(blueprint);
    if (!validation.valid) {
        return Object.freeze({
            valid: false,
            blueprint: null,
            issues: Object.freeze(validation.issues.map((entry) => Object.freeze({
                code: entry.code,
                path: entry.path,
                message: entry.message,
            }))),
        });
    }
    return Object.freeze({
        valid: true,
        blueprint,
        issues: Object.freeze([]),
    });
}
//# sourceMappingURL=index.js.map